import { PaperSize } from "@client-shared/types/pdfPrint";

const SMALL_PAPER_WIDTH_THRESHOLD = 250; // Width threshold for 3x4 paper vs larger sizes

export type Dimensions = {
  width: number;
  height: number;
  margin: number;
  titleMaxLength: number;
  detailsMaxLength: number;
  addressMaxLength: number;
};

export const getDimensions = (size: PaperSize): Dimensions => {
  switch (size) {
    case "A6":
      return { width: 293, height: 415, margin: 15, titleMaxLength: 26, detailsMaxLength: 55, addressMaxLength: 34 };
    case "A4":
      return { width: 595, height: 842, margin: 20, titleMaxLength: 50, detailsMaxLength: 100, addressMaxLength: 70 };
    case "Letter":
      return { width: 612, height: 792, margin: 20, titleMaxLength: 50, detailsMaxLength: 100, addressMaxLength: 70 };
    case "4x6":
      return { width: 288, height: 432, margin: 12, titleMaxLength: 24, detailsMaxLength: 48, addressMaxLength: 32 };
    case "3x4":
      return { width: 216, height: 288, margin: 10, titleMaxLength: 18, detailsMaxLength: 36, addressMaxLength: 24 };
    default:
      return { width: 612, height: 792, margin: 20, titleMaxLength: 50, detailsMaxLength: 100, addressMaxLength: 70 };
  }
};

// Common scaling utilities for small paper
export const isSmallPaper = (dimensions: Dimensions): boolean => {
  return dimensions.width < SMALL_PAPER_WIDTH_THRESHOLD;
};

// Helper function to calculate font size with small paper scaling
export const getScaledFontSize = (baseFontSize: number, dimensions: Dimensions): number => {
  return isSmallPaper(dimensions) ? baseFontSize * 0.7 : baseFontSize;
};

// Helper function to get scaled spacing values for small paper
export const getScaledSpacing = (normalSpacing: number, smallSpacing: number, dimensions: Dimensions): number => {
  return isSmallPaper(dimensions) ? smallSpacing : normalSpacing;
};

// Helper function to get scaled font size for small paper
export const getScaledFontSizeForSmallPaper = (normalFontSize: number, smallFontSize: number, dimensions: Dimensions): number => {
  return isSmallPaper(dimensions) ? smallFontSize : normalFontSize;
};

/**
 * Shared helper to calculate column widths and positions based on dimensions.
 * This eliminates duplication between getLocalizedTableColumns and drawLineItemRow.
 */
export const calculateColumnDimensions = (dimensions: Dimensions) => {
  const isSmallPaper = dimensions.width < SMALL_PAPER_WIDTH_THRESHOLD; // 3x4 paper
  const qtyWidth = isSmallPaper ? 18 : 25;
  const weightWidth = isSmallPaper ? 30 : 40;
  const grindWidth = isSmallPaper ? 45 : 61;
  const columnSpacing = 3; // Add spacing between columns

  // Calculate available width for Item # and Description columns
  // Total width minus margins, fixed columns (qty, weight, grind), and spacing
  const totalSpacing = columnSpacing * 4; // Spacing between: qty-item, item-desc, desc-weight, weight-grind
  const fixedColumnsWidth = qtyWidth + weightWidth + grindWidth;
  const availableWidth = dimensions.width - dimensions.margin * 2 - fixedColumnsWidth - totalSpacing;

  // Minimum widths (current widths as minimums)
  const minItemWidth = isSmallPaper ? 30 : 45;
  const minDescWidth = isSmallPaper ? 80 : 110;

  // Calculate Item # and Description as percentages of available width
  // Item # gets 25% of available width, Description gets more space at larger sizes
  // At smaller sizes, use more conservative percentages
  const itemPercent = 0.25; // 25% for Item #
  const descPercent = isSmallPaper ? 0.45 : 0.5; // 45-50% for Description (more space at larger sizes)

  // Calculate widths using percentages, but ensure minimums are met
  let itemWidth = Math.max(minItemWidth, Math.floor(availableWidth * itemPercent));
  let descWidth = Math.max(minDescWidth, Math.floor(availableWidth * descPercent));

  // Ensure total doesn't exceed available width (prioritize Description if needed)
  const totalFlexibleWidth = itemWidth + descWidth;
  if (totalFlexibleWidth > availableWidth) {
    // If both are at minimums and exceed available, scale them proportionally
    // but keep Description larger than Item #
    const scale = availableWidth / totalFlexibleWidth;
    itemWidth = Math.max(minItemWidth, Math.floor(itemWidth * scale));
    descWidth = Math.max(minDescWidth, Math.floor(descWidth * scale));

    // Final check: if still exceeding, prioritize Description
    if (itemWidth + descWidth > availableWidth) {
      // Try to keep descWidth at least minDescWidth
      descWidth = Math.max(minDescWidth, availableWidth - itemWidth);
      // If still not enough space for both minimums, reduce itemWidth if possible
      if (itemWidth + descWidth > availableWidth) {
        itemWidth = Math.max(minItemWidth, availableWidth - minDescWidth);
        descWidth = Math.max(minDescWidth, availableWidth - itemWidth);
      }
      // If availableWidth is less than sum of minimums, both will be at their minimums
    }
  }

  // Adjust positioning: qty moves left, grind moves right
  const qtyPosition = dimensions.margin - (isSmallPaper ? 3 : 5) + 3; // Move qty left, then add 3px spacing from border
  const itemPosition = qtyPosition + qtyWidth + columnSpacing; // Add spacing after qty column
  const descPosition = itemPosition + itemWidth + columnSpacing; // Add spacing after item column
  const grindPosition = dimensions.width - dimensions.margin - grindWidth + (isSmallPaper ? 5 : 8); // Move grind right

  return {
    isSmallPaper,
    qtyWidth,
    itemWidth,
    descWidth,
    weightWidth,
    grindWidth,
    columnSpacing,
    qtyPosition,
    itemPosition,
    descPosition,
    grindPosition,
  };
};
